import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Buildings, Kanban, Warning, Clock, CheckCircle, ArrowRight } from '@phosphor-icons/react';
import { Badge } from '@/components/ui/badge';
import { useT } from '@/i18n/LanguageContext';
import { plannerApi } from './plannerApi';
import { groupDepartments } from './plannerConstants';
import AlertList from './components/AlertList';

export default function PlannerHome() {
  const { t } = useT();
  const navigate = useNavigate();
  const [departments, setDepartments] = useState([]);
  const [alerts, setAlerts] = useState({
    overdue: [], due_soon: [], my_open: [], my_open_count: 0, my_blocked_count: 0,
  });

  useEffect(() => {
    plannerApi.departments().then(setDepartments).catch(() => {});
    plannerApi.alerts().then(setAlerts).catch(() => {});
  }, []);

  return (
    <div className="space-y-6" data-testid="planner-home">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-zinc-900">{t('app.planner')}</h1>
        <p className="text-sm text-zinc-500 mt-1">{t('planner.home.subtitle')}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <AlertList testid="alerts-overdue" title={t('planner.alerts.overdue')} Icon={Warning} color="text-rose-500"
          items={alerts.overdue || []} allHref="/planner/my-tasks?scope=all&filter=overdue" />
        <AlertList testid="alerts-duesoon" title={t('planner.alerts.dueSoon')} Icon={Clock} color="text-amber-500"
          dueClass="text-amber-600"
          items={alerts.due_soon || []} allHref="/planner/my-tasks?scope=all&filter=due_soon" />
        {/* B8 — ヘッダーのバッジ・サイドバーの要対応と同じ数（自分の未完了・ブロック中も含む） */}
        <AlertList testid="alerts-mine" title={t('planner.alerts.mineOpen')} Icon={CheckCircle} color="text-indigo-500"
          items={alerts.my_open || []} allHref="/planner/my-tasks"
          note={(alerts.my_blocked_count || 0) > 0 ? t('planner.alerts.blockedNote', { n: alerts.my_blocked_count }) : null} />
      </div>

      <div>
        <h2 className="text-base font-bold text-zinc-800 mb-3">{t('planner.nav.departments')}</h2>
        <div className="space-y-6">
          {groupDepartments(departments).map((g) => (
            <div key={g.key} data-testid={`planner-dept-group-${g.key}`}>
              <div className="flex items-center gap-2 mb-2.5">
                <span className="text-sm font-bold text-indigo-600">{g.label}</span>
                <span className="h-px flex-1 bg-zinc-200" />
                <span className="text-[11px] text-zinc-400 tabular-nums">{g.items.length}</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 stagger-children">
                {g.items.map((d) => (
                  <button key={d.id} onClick={() => navigate(`/planner/dept/${d.id}`)} data-testid={`planner-dept-card-${d.id}`}
                    className="group text-left bg-white rounded-xl border border-zinc-200 p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 hover:border-indigo-200 transition-all">
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-11 h-11 rounded-xl bg-indigo-50 flex items-center justify-center">
                        <Buildings size={22} weight="duotone" className="text-indigo-600" />
                      </div>
                      <ArrowRight size={16} className="text-zinc-300 group-hover:text-indigo-500 transition-colors" />
                    </div>
                    <h3 className="text-sm font-semibold text-zinc-900 truncate">{d.name}</h3>
                    <div className="flex items-center gap-3 mt-2 text-xs text-zinc-500">
                      <span className="inline-flex items-center gap-1"><Kanban size={13} weight="duotone" /> {t('planner.home.projects', { n: d.project_count })}</span>
                      {d.active_count > 0 && <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200 text-[10px] px-1.5 py-0">{t('planner.home.active', { n: d.active_count })}</Badge>}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
